import { Search, SlidersHorizontal } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import "./SearchBarWithFilter.css";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../redux/snackbarSlice";

const SearchBarWithFilter = ({
  searchQuery,
  setSearchQuery,
  columnOptions = [], // default to empty array if not provided
  setSearchType,
  searchType,
  setSelectedColumns,
  selectedColumns,
}) => {
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const columnOptionsRef = useRef(null);
  const dispatch = useDispatch();

  const snackbar = (message, type) => {
    dispatch(showSnackbar({ type: type, message: message }));
  };

  const searchOptions = [
    { id: "all_columns", label: "All Columns", type: "radio" },
    { id: "select_columns", label: "Select Columns", type: "radio" },
  ];

  const isAnyColumnSelected = Object.values(selectedColumns).some(
    (value) => value
  );
  const isFilterActive = searchType === "select_columns" && isAnyColumnSelected;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSearchDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Calculate and set max-height for dropdown and column-options based on available space
  useEffect(() => {
    if (showSearchDropdown && dropdownRef.current && columnOptionsRef.current) {
      const calculateMaxHeight = () => {
        const dropdown = dropdownRef.current;
        const columnOptions = columnOptionsRef.current;

        if (!dropdown || !columnOptions) return;

        // Get dropdown position relative to viewport
        const dropdownRect = dropdown.getBoundingClientRect();
        const dropdownTop = dropdownRect.top;

        // Validate dropdown position (must be visible)
        if (dropdownTop < 0 || dropdownTop > window.innerHeight) {
          // Dropdown is outside viewport, use a safe default
          const safeHeight = Math.min(400, window.innerHeight * 0.6);
          columnOptions.style.setProperty(
            "max-height",
            `${safeHeight}px`,
            "important"
          );
          dropdown.style.setProperty(
            "max-height",
            `${safeHeight + 100}px`,
            "important"
          );
          return;
        }

        // Find the closest popup container first (for popup contexts like PartMentionsPopup or SelectRevisionModal)
        let containerBottom = window.innerHeight;
        let containerElement =
          dropdown.closest(".popup-card") ||
          dropdown.closest(".popup-body") ||
          dropdown.closest(".popup-overlay") ||
          dropdown.closest(".modal-content") ||
          dropdown.closest(".modal");

        if (containerElement) {
          // We're inside a popup/modal - use container boundaries
          const containerRect = containerElement.getBoundingClientRect();
          containerBottom = containerRect.bottom;

          // Ensure container bottom doesn't exceed viewport and is valid
          containerBottom = Math.min(containerBottom, window.innerHeight);

          // If container bottom is above dropdown, something's wrong - use viewport
          if (containerBottom <= dropdownTop) {
            containerBottom = window.innerHeight;
          }
        } else {
          // We're NOT inside a popup - use viewport bottom
          containerBottom = window.innerHeight;
        }

        // Calculate available space from dropdown top to container bottom
        // Leave padding for better UX (30px from bottom to ensure scroll works properly)
        const paddingFromBottom = 30;
        const rawAvailableSpace =
          containerBottom - dropdownTop - paddingFromBottom;

        // Ensure minimum available space for scrolling to work (at least 150px)
        // Also cap at reasonable maximum (80% of viewport height)
        const maxAllowed = window.innerHeight * 0.8;
        const availableSpace = Math.max(
          150,
          Math.min(maxAllowed, rawAvailableSpace)
        );

        // Get the height of search-options section (radio buttons)
        const searchOptionsElement = dropdown.querySelector(".search-options");
        const searchOptionsHeight = searchOptionsElement
          ? Math.max(60, searchOptionsElement.offsetHeight)
          : 80;

        // Calculate max height for column-options
        // (available space minus search options height minus extra padding for scroll)
        const extraPadding = 30; // Extra padding to ensure last items are fully visible
        const maxColumnOptionsHeight = Math.max(
          150,
          availableSpace - searchOptionsHeight - extraPadding
        );

        // Set max-height for column-options dynamically with !important to override CSS
        columnOptions.style.setProperty(
          "max-height",
          `${maxColumnOptionsHeight}px`,
          "important"
        );

        // Also set max-height for dropdown container to ensure proper scrolling
        // This should be slightly larger than the column-options to account for padding
        const dropdownMaxHeight = Math.max(
          200,
          searchOptionsHeight + maxColumnOptionsHeight + 20
        );
        dropdown.style.setProperty(
          "max-height",
          `${dropdownMaxHeight}px`,
          "important"
        );
      };

      // Calculate on mount and when dropdown opens - use multiple methods to ensure DOM is ready
      // Use requestAnimationFrame to ensure calculation happens after browser paint
      const rafId = requestAnimationFrame(() => {
        calculateMaxHeight();
        // Also recalculate after a short delay to ensure everything is settled
        setTimeout(calculateMaxHeight, 0);
        setTimeout(calculateMaxHeight, 10);
        setTimeout(calculateMaxHeight, 50);
      });

      const timeoutId1 = setTimeout(calculateMaxHeight, 0);
      const timeoutId2 = setTimeout(calculateMaxHeight, 10);
      const timeoutId3 = setTimeout(calculateMaxHeight, 50);

      // Recalculate on window resize or scroll
      const handleResize = () => {
        // Debounce resize handler slightly
        setTimeout(calculateMaxHeight, 10);
      };
      const handleScroll = () => {
        // Debounce scroll handler slightly
        setTimeout(calculateMaxHeight, 10);
      };

      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", handleScroll, true); // Use capture to catch all scroll events

      // Also listen for popup body scroll if it exists
      const popupBody = dropdownRef.current?.closest(".popup-body");
      if (popupBody) {
        popupBody.addEventListener("scroll", handleScroll);
      }

      return () => {
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("scroll", handleScroll, true);
        if (popupBody) {
          popupBody.removeEventListener("scroll", handleScroll);
        }
        cancelAnimationFrame(rafId);
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        clearTimeout(timeoutId3);
        // Reset max-height when dropdown is closed
        if (columnOptionsRef.current) {
          columnOptionsRef.current.style.removeProperty("max-height");
        }
        if (dropdownRef.current) {
          dropdownRef.current.style.removeProperty("max-height");
        }
      };
    }
  }, [showSearchDropdown]);

  const handleSearchTypeChange = (type) => {
    setSearchType(type);
    if (type === "all_columns") {
      // Select all columns when switching to "All Columns"
      const allColumnsSelected = {};
      columnOptions.forEach((column) => {
        allColumnsSelected[column.id] = true;
      });
      setSelectedColumns(allColumnsSelected);
    }
  };

  const handleColumnToggle = (columnId, event) => {
    // Prevent double-toggling when clicking directly on checkbox
    if (event && event.target.type === "checkbox") {
      event.stopPropagation();
    }

    // Prevent toggling when "All Columns" is selected
    if (searchType !== "all_columns") {
      setSelectedColumns((prev) => {
        let updated = { ...prev, [columnId]: !prev[columnId] };
        // Validate that at least one column is selected
        if (Object.values(updated).filter((c) => c === true).length === 0) {
          snackbar(
            "Please keep at least one column selected to apply the search filter.",
            "error"
          );
          return prev;
        } else {
          return updated;
        }
      });
    }
  };

  return (
    <div className="search-bar-wrapper" ref={wrapperRef}>
      <div className="search-bar">
        <div
          className={`search-icon-left ${
            showSearchDropdown || isFilterActive ? "active" : ""
          }`}
          onClick={() => setShowSearchDropdown(!showSearchDropdown)}
        >
          <SlidersHorizontal
            size={18}
            color={showSearchDropdown || isFilterActive ? "#0D6EFD" : "#fff"}
          />
        </div>

        <input
          type="text"
          placeholder="Type to Search"
          className="search-input"
          onClick={() => setShowSearchDropdown(false)}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <div className="search-icon-right">
          {searchQuery.length === 0 ? (
            <Search size={18} color="#999" />
          ) : (
            <span
              style={{ cursor: "pointer" }}
              onClick={() => setSearchQuery("")}
              className="app-table-cancel-search"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.4 14L0 12.6L5.6 7L0 1.4L1.4 0L7 5.6L12.6 0L14 1.4L8.4 7L14 12.6L12.6 14L7 8.4L1.4 14Z"
                  fill="#6C757D"
                />
              </svg>
            </span>
          )}
        </div>
      </div>

      {showSearchDropdown && (
        <div className="search-dropdown" ref={dropdownRef}>
          <div className="search-options">
            {searchOptions.map((option) => (
              <div
                key={option.id}
                className="search-option"
                onClick={() => handleSearchTypeChange(option.id)}
              >
                <input
                  type="radio"
                  id={option.id}
                  name="searchType"
                  checked={searchType === option.id}
                  onChange={() => handleSearchTypeChange(option.id)}
                />
                <label htmlFor={option.id}>{option.label}</label>
              </div>
            ))}
          </div>

          <div className="column-options" ref={columnOptionsRef}>
            {columnOptions.map((column) => (
              <div
                key={column.id}
                className="column-option"
                onClick={(e) => {
                  // Only handle click if it's not directly on the checkbox or label
                  // (label clicks will trigger checkbox onChange via htmlFor)
                  if (
                    e.target.type !== "checkbox" &&
                    e.target.tagName !== "LABEL"
                  ) {
                    handleColumnToggle(column.id, e);
                  }
                }}
              >
                <input
                  type="checkbox"
                  id={column.id}
                  checked={
                    selectedColumns[column.id] || searchType === "all_columns"
                  }
                  onChange={(e) => handleColumnToggle(column.id, e)}
                  disabled={searchType === "all_columns"}
                  onClick={(e) => e.stopPropagation()}
                />
                <label htmlFor={column.id} onClick={(e) => e.stopPropagation()}>
                  {column.label}
                </label>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBarWithFilter;
